import { useUnitsFormatter } from "hooks/formatters";
import { Layout, PlotData } from "plotly.js";
import { useMemo } from "react";
import Plot from "components/plotly/LocalisedPlot";
import { LegendHint } from "../LegendHint";
import { TimeAggregation } from "src/store/api/generatedApi";
import { defaultLayout, getColorBySource } from "../plotting";
import { GroupedScheduleSeries } from "../types";
import { useTranslation } from "react-i18next";

interface SchedulePlotProps {
  scheduleSeries: GroupedScheduleSeries;
  selectedTimeAggregation: TimeAggregation;
  selectedMixGroups: string[];
}

export const SchedulePlot = ({
  scheduleSeries,
  selectedTimeAggregation,
  selectedMixGroups,
}: SchedulePlotProps) => {
  const { t } = useTranslation();
  const units = useUnitsFormatter(true);

  const layout: Partial<Layout> = useMemo(
    () => ({
      ...defaultLayout,
      barmode: "stack" as const,
      yaxis: {
        ...defaultLayout.yaxis,
        title: { text: `${t("mass")} ${units("mass")}` },
      },
    }),
    [t, units]
  );

  const data = useMemo(
    () =>
      buildFigData(
        scheduleSeries,
        selectedTimeAggregation,
        selectedMixGroups,
        t
      ),
    [scheduleSeries, selectedTimeAggregation, selectedMixGroups, t]
  );

  return (
    <div style={{ width: "100%" }}>
      <Plot
        data={data}
        layout={layout}
        style={{ width: "100%" }}
        useResizeHandler
      />
      <LegendHint />
    </div>
  );
};

const buildFigData = (
  scheduleSeries: GroupedScheduleSeries,
  selectedTimeAggregation: TimeAggregation,
  selectedMixGroups: string[],
  t: (key: string) => string
): Partial<PlotData>[] =>
  scheduleSeries.availableMixGroups
    .filter((mixGroup) => selectedMixGroups.includes(mixGroup))
    .flatMap((mixGroup) => {
      const entry = scheduleSeries.items.find(
        ([key]) =>
          key.timeAggregation === selectedTimeAggregation &&
          key.mixGroup === mixGroup
      );
      if (!entry) return [];
      const [, series] = entry;
      const color = getColorBySource(
        scheduleSeries.availableMixGroups,
        mixGroup
      );
      return [
        {
          x: series.date,
          y: series.scheduled_mass,
          type: "bar" as const,
          name: t(mixGroup),
          marker: { color },
        },
      ];
    });
